import type {
	CredentialEntry,
	PersistedCredentialEntry,
	PersistedAuthSessionV2,
	ScopeKey,
} from './types';
import { makeScopeKey, normalizeScope } from './scope';

export const MAX_CREDENTIAL_ENTRIES = 8;
export const FALLBACK_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const isFiniteTimestamp = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value >= 0;

const parseJwtExpiry = (refreshToken: string): number | null => {
	try {
		const payload = refreshToken.split('.')[1];
		if (!payload) return null;
		const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as unknown;
		if (
			typeof decoded !== 'object' ||
			decoded === null ||
			!('exp' in decoded) ||
			typeof decoded.exp !== 'number' ||
			!Number.isFinite(decoded.exp)
		) {
			return null;
		}
		return decoded.exp * 1000;
	} catch {
		return null;
	}
};

export const resolveExpiry = (refreshToken: string, acquiredAt: number): number =>
	parseJwtExpiry(refreshToken) ?? acquiredAt + FALLBACK_EXPIRY_MS;

const isPersistedEntry = (value: unknown): value is PersistedCredentialEntry => {
	if (typeof value !== 'object' || value === null) return false;
	const entry = value as Record<string, unknown>;
	const scope = normalizeScope(
		entry['companyId'],
		entry['delegationIds'] as readonly unknown[] | undefined,
	);
	return (
		scope !== null &&
		typeof entry['accessToken'] === 'string' &&
		typeof entry['refreshToken'] === 'string' &&
		typeof entry['revision'] === 'number' &&
		Number.isSafeInteger(entry['revision']) &&
		entry['revision'] > 0 &&
		isFiniteTimestamp(entry['acquiredAt']) &&
		isFiniteTimestamp(entry['lastUsedAt']) &&
		isFiniteTimestamp(entry['expiresAt'])
	);
};

export const pruneEntries = (
	entries: Record<string, unknown>,
	now: number,
): Record<ScopeKey, PersistedCredentialEntry> => {
	const validEntries = Object.entries(entries)
		.flatMap(([key, value]) => {
			if (!isPersistedEntry(value) || value.expiresAt <= now) return [];
			const scope = normalizeScope(value.companyId, value.delegationIds);
			if (scope === null || makeScopeKey(scope) !== key) return [];
			return [
				[
					key as ScopeKey,
					{ ...value, companyId: scope.companyId, delegationIds: [...scope.delegationIds] },
				] as const,
			];
		})
		.sort(([, left], [, right]) => right.lastUsedAt - left.lastUsedAt)
		.slice(0, MAX_CREDENTIAL_ENTRIES);

	return Object.fromEntries(validEntries) as Record<ScopeKey, PersistedCredentialEntry>;
};

export const prunePersistedSession = (
	value: unknown,
	now: number,
): PersistedAuthSessionV2 | null => {
	if (typeof value !== 'object' || value === null) return null;
	const session = value as Record<string, unknown>;
	if (
		session['version'] !== 2 ||
		typeof session['loginEpoch'] !== 'number' ||
		typeof session['nextRevision'] !== 'number' ||
		typeof session['isSuperUser'] !== 'boolean' ||
		typeof session['entries'] !== 'object' ||
		session['entries'] === null
	) {
		return null;
	}
	// isStaff is additive (post-dates this persisted schema version) — tolerate
	// its absence in older payloads rather than invalidating the whole session.
	const isStaff = session['isStaff'] === true;

	const entries = pruneEntries(session['entries'] as Record<string, unknown>, now);
	const activeScopeKey =
		typeof session['activeScopeKey'] === 'string' && entries[session['activeScopeKey'] as ScopeKey]
			? (session['activeScopeKey'] as ScopeKey)
			: null;
	const lastUsableScopeKey =
		typeof session['lastUsableScopeKey'] === 'string' &&
		entries[session['lastUsableScopeKey'] as ScopeKey]
			? (session['lastUsableScopeKey'] as ScopeKey)
			: activeScopeKey;

	return {
		version: 2,
		loginEpoch: Math.max(0, Math.floor(session['loginEpoch'])),
		nextRevision: Math.max(1, Math.floor(session['nextRevision'])),
		isSuperUser: session['isSuperUser'],
		isStaff,
		activeScopeKey,
		lastUsableScopeKey,
		entries,
	};
};

export const omitIdTokens = (
	entries: Record<ScopeKey, CredentialEntry>,
	now: number,
): Record<ScopeKey, PersistedCredentialEntry> =>
	pruneEntries(
		Object.fromEntries(
			Object.entries(entries).map(([key, entry]) => {
				const { idToken: _idToken, scope, ...persistedEntry } = entry;
				return [
					key,
					{
						...persistedEntry,
						companyId: scope.companyId,
						delegationIds: scope.delegationIds,
					},
				];
			}),
		),
		now,
	);
